import _ from "lodash";
import React from "react";
import { canonicalIdForHSProperty, HGObject } from "../../domain";
import { HSProperty, HSPropertyEnumeration } from "../../domain/property";
import { hsPropertyCanSelfResolveValues } from "../../domain/resolved-values";
import { useStore } from "../../hooks/hooks";
import * as computeds from "../../computeds";
import { observer } from "mobx-react-lite";
import {
  resolvePropertyValue,
  optionValueToLabel,
  formatValueForDisplay,
} from "./helpers";
import * as db from "../../store/db";

const SidebarPropertyLabel = (props: { children: string }) => {
  const { children } = props;
  return <div className="text-sm font-normal text-slate-600">{children}</div>;
};

const SidebarPropertyRowNull: React.FC<{
  hsProperty: HSProperty;
  value: null;
}> = (props) => {
  const { hsProperty } = props;

  return (
    <div className="flex flex-col space-y-1">
      <SidebarPropertyLabel>{hsProperty.label}</SidebarPropertyLabel>
    </div>
  );
};

const SidebarPropertyRowDefault: React.FC<{
  hsProperty: HSProperty;
  hgObject: HGObject;
  value: string;
}> = observer((props) => {
  const { hsProperty, hgObject, value } = props;

  const store = useStore();

  console.log("value", value);

  const resolvedValue = resolvePropertyValue({
    store,
    hsProperty,
    hgObject,
    value: value,
  });

  const formattedValue = formatValueForDisplay({
    hsProperty,
    hgObject,
    value: resolvedValue,
    hgAccountDetails: db.getAccountDetails(store),
  });

  console.log("resolvedValue", resolvedValue);

  return (
    <div className="flex flex-col space-y-1">
      <SidebarPropertyLabel>{hsProperty.label}</SidebarPropertyLabel>
      <div className="text-base font-normal text-slate-700 hg-privacy blur">
        {formattedValue}
      </div>
    </div>
  );
});

const SidebarPropertyRowEnumeration: React.FC<{
  hsProperty: HSPropertyEnumeration;
  hgObject: HGObject;
  value: string;
}> = observer((props) => {
  const { hsProperty, hgObject, value } = props;

  const store = useStore();

  const values = value.split(";");

  let optionsForRender: { label: string; value: string }[] = [];
  if (hsPropertyCanSelfResolveValues(hsProperty)) {
    optionsForRender = _.map(values, (optionValue) => {
      const valueLabel = optionValueToLabel({
        hsProperty,
        value: optionValue,
      });
      return { label: valueLabel || value, value: optionValue };
    });
  } else {
    optionsForRender = _.map(values, (optionValue) => {
      const valueLabel = resolvePropertyValue({
        store,
        hsProperty,
        hgObject,
        value: optionValue,
      });
      return { label: valueLabel, value: optionValue };
    });
  }

  const Tag = (props: { label: string }) => {
    return (
      <div className="px-2 py-1 bg-slate-200 rounded-md inline-flex items-center justify-center hg-privacy blur">
        {props.label}
      </div>
    );
  };

  return (
    <div className="flex flex-col space-y-1">
      <SidebarPropertyLabel>{hsProperty.label}</SidebarPropertyLabel>
      <div className="grow flex-row flex-wrap space-x-2 text-base leading-normal font-normal text-slate-800">
        {_.map(optionsForRender, (option) => {
          return <Tag key={option.value} label={option.label} />;
        })}
      </div>
    </div>
  );
});

const SidebarPropertyRowDateOrDateTime: React.FC<{
  hsProperty: HSProperty;
  hgObject: HGObject;
  value: string;
}> = observer((props) => {
  const { hsProperty, hgObject, value } = props;

  const store = useStore();

  const formattedValue = formatValueForDisplay({
    hsProperty,
    hgObject,
    value,
    hgAccountDetails: db.getAccountDetails(store),
  });

  return (
    <div className="flex flex-col space-y-1">
      <SidebarPropertyLabel>{hsProperty.label}</SidebarPropertyLabel>
      <div className="grow truncate hg-privacy blur text-base leading-normal text-slate-800">
        {formattedValue}
      </div>
    </div>
  );
});

const SidebarPropertyRow: React.FC<{
  hgObject: HGObject;
  propertyName: string;
  propertyValue: string | null;
}> = (props) => {
  const { hgObject, propertyName, propertyValue } = props;

  const store = useStore();

  const hsProperty = store.hgProperties[
    canonicalIdForHSProperty({
      objectType: hgObject.objectType,
      name: propertyName,
    })
  ] as HSProperty | undefined;

  if (typeof hsProperty === "undefined") {
    return <div>can't find property: {propertyName}</div>;
  }

  let reactNode: React.ReactNode;

  if (propertyValue === null) {
    reactNode = (
      <SidebarPropertyRowNull hsProperty={hsProperty} value={propertyValue} />
    );
  } else {
    switch (hsProperty.type) {
      case "datetime": {
        reactNode = (
          <SidebarPropertyRowDateOrDateTime
            hsProperty={hsProperty}
            hgObject={hgObject}
            value={propertyValue}
          />
        );
        break;
      }

      case "date": {
        reactNode = (
          <SidebarPropertyRowDateOrDateTime
            hsProperty={hsProperty}
            hgObject={hgObject}
            value={propertyValue}
          />
        );
        break;
      }

      case "enumeration": {
        reactNode = (
          <SidebarPropertyRowEnumeration
            hsProperty={hsProperty}
            hgObject={hgObject}
            value={propertyValue}
          />
        );
        break;
      }

      default: {
        reactNode = (
          <SidebarPropertyRowDefault
            hsProperty={hsProperty}
            hgObject={hgObject}
            value={propertyValue}
          />
        );
      }
    }
  }

  return <div className="px-3 p-2">{reactNode}</div>;
};

export const SidebarPropertyRows: React.FC<{
  hgObject: HGObject;
}> = observer((props) => {
  const { hgObject } = props;

  const displayProperties = computeds.portalDisplayPropertiesOnCard(
    hgObject.objectType,
  );

  return (
    <div className="divide-y-2 divide-slate-200">
      {_.map(displayProperties, (displayProperty) => {
        const value = hgObject.properties?.[displayProperty.name] || null;

        if (value === null) {
          return null;
        }

        return (
          <SidebarPropertyRow
            key={displayProperty.name}
            hgObject={hgObject}
            propertyName={displayProperty.name}
            propertyValue={value}
          />
        );
      })}
    </div>
  );
});
